import {
  type Color,
  MASKED,
  MASK_CHALLENGE,
  MASK_READY,
  NOTE_CAUGHT,
  NOTE_CLEARED,
  NOTE_KING_SAVED,
  PHASE_PLAY,
  type Position,
  TYPE_MASK,
  colorBit,
  isSeen,
  maskNote,
  maskPhase,
  movePromo,
  pieceColor,
} from '@chessbut/engine';
import type { ActionButton, Decoration, VariantUi } from './ui.ts';

const MASK = '/art/mask.svg';
const isChallenge = (m: number) => movePromo(m) === MASK_CHALLENGE;
const isReady = (m: number) => movePromo(m) === MASK_READY;

export const masqueradeUi: VariantUi = {
  heroImage: MASK,
  peek: [{}, { image: '/art/mask-b.svg' }, {}, {}, { image: '/art/mask-b.svg', threat: true }, {}, {}, { image: '/art/wN.svg' }, {}],
  secret: true,
  hideMaterial: true,

  boardAction: (m) => !isChallenge(m) && !isReady(m),

  actions(_pos, legal) {
    const out: ActionButton[] = [];
    const ready = legal.find(isReady);
    if (ready !== undefined) out.push({ label: 'Ready', move: ready, tone: 'strong', title: 'Lock in your setup' });
    const challenge = legal.find(isChallenge);
    if (challenge !== undefined) out.push({ label: 'Challenge!', move: challenge, tone: 'danger', title: 'Could that piece really move like that?' });
    return out;
  },

  view(pos: Position, viewer: Color | null): Position {
    if (viewer === null) return pos;
    const board = pos.board.slice();
    for (let sq = 0; sq < 64; sq++) {
      const p = board[sq];
      const c = pieceColor(p);
      if (c === -1 || c === viewer || isSeen(pos, sq)) continue;
      board[sq] = MASKED | colorBit(c);
    }
    return { ...pos, board };
  },

  decorations(pos) {
    // Your pieces the other side has seen.
    const out: Decoration[] = [];
    for (let sq = 0; sq < 64; sq++) {
      const p = pos.board[sq];
      if (p && (p & TYPE_MASK) !== MASKED && isSeen(pos, sq)) out.push({ kind: 'badge', sq, text: '👁', tone: 'normal' });
    }
    return out;
  },

  pieceLabel(_pos, _sq, piece) {
    return (piece & TYPE_MASK) === MASKED ? `masked ${pieceColor(piece) === 0 ? 'white' : 'black'} piece` : null;
  },

  twist(pos, you) {
    const mine = you === null || pos.turn === you;
    if (maskPhase(pos) < PHASE_PLAY) {
      return mine
        ? { text: 'Arrange your army in secret: tap two of your pieces to swap them, then press Ready.', pips: { on: maskPhase(pos) + 1, total: 2, title: 'White sets up, then Black' } }
        : { text: 'They’re arranging their army in secret…', pips: { on: maskPhase(pos) + 1, total: 2, title: 'White sets up, then Black' } };
    }
    const note = maskNote(pos);
    if (note === NOTE_CAUGHT) return { text: mine ? 'Caught bluffing! Their move is taken back and it’s your turn.' : 'Caught! That piece couldn’t move like that.', alert: true };
    if (note === NOTE_CLEARED) return { text: mine ? 'No bluff! Their challenge failed, so you go again.' : 'It was real. The challenger loses their turn.', alert: true };
    if (note === NOTE_KING_SAVED) return { text: 'A bluff can’t take a king. Taken back.', alert: true };
    return { text: mine ? 'Any piece can move like any piece. Bluff, or challenge their last move.' : 'Watch their moves. Was that a bluff?' };
  },

  eventSound(e) {
    if (e.t !== 'fx') return null;
    return e.kind === 'caught' ? 'betray' : e.kind === 'cleared' ? 'boing' : null;
  },

  moveBadge(rec) {
    return rec.captured ? { icon: MASK, victim: rec.captured, title: 'Captured and unmasked' } : null;
  },

  hideInMoveList(rec) {
    // Setup swaps stay secret.
    return rec.san === 'swap';
  },
};
